/**
 * @fileoverview Anchored comment thread (F9 / Spec 06).
 *
 * ONE component, mounted on both sides of the product. The two audiences
 * differ only in which axios client and which path prefix they hand in — the
 * server makes every access decision in one place, and a second component
 * would be a second place for the two to drift about who may say what.
 *
 * It renders nothing until the thread resolves, and nothing at all if the
 * fetch fails: the endpoints 404 behind `COMMENTS_ENABLED`, which is how a
 * flag flip reaches a build that has already shipped.
 *
 * Three details that are decisions rather than styling:
 *
 * - A deleted comment keeps its slot and shows a placeholder. A message that
 *   silently vanishes reads as a bug, or worse as something being hidden.
 * - The composer is hidden — not disabled — when `can_post` is false. A box
 *   you can type into and cannot send from is a worse answer than no box.
 * - Marking read happens once per mount, after the thread arrives, so opening
 *   a lesson clears its badge without a second round trip.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Card, SectionHeader, Button, Icon } from "@/components/studio/primitives";

/** Minutes an author may edit their own comment — mirrors the server. */
export const EDIT_WINDOW_MINUTES = 15;

export function withinEditWindow(createdAt, now = Date.now()) {
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return now - created <= EDIT_WINDOW_MINUTES * 60 * 1000;
}

function whenLabel(iso) {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function CommentThread({ client, basePath, artifactKind, artifactId, title = "Conversation" }) {
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const markedRead = useRef(false);

  const url = `${basePath}/${artifactKind}/${artifactId}`;

  const load = useCallback(async () => {
    try {
      const { data } = await client.get(url);
      // A response that isn't a thread is the same answer as no thread. This
      // is a section on somebody else's page, so anything short of a real
      // payload has to render nothing rather than take the lesson down with
      // it — the failure mode a plain `setThread(data)` produced.
      if (!Array.isArray(data?.comments)) {
        setThread(false);
        return;
      }
      setThread(data);
      if (!markedRead.current) {
        markedRead.current = true;
        // Best-effort: a badge that lingers one reload is a nuisance, a
        // thread that fails to render because of it is a bug.
        client.post(`${url}/read`).catch(() => {});
      }
    } catch {
      setThread(false);
    }
  }, [client, url]);

  useEffect(() => {
    load();
  }, [load]);

  if (thread === null || thread === false) return null;

  const post = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await client.post(url, { body });
      setDraft("");
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't post that"));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (commentId) => {
    const body = editDraft.trim();
    if (!body) return;
    try {
      await client.patch(`${url}/${commentId}`, { body });
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't save that edit"));
    }
  };

  const remove = async (commentId) => {
    try {
      await client.delete(`${url}/${commentId}`);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't remove that"));
    }
  };

  const mine = (c) => c.author_kind === thread.viewer_kind;

  return (
    <Card className="mt-6" data-testid="comment-thread">
      <SectionHeader>{title}</SectionHeader>

      {thread.comments.length === 0 ? (
        <p className="font-sans text-[14px] text-[var(--text-tertiary)]" data-testid="comments-empty">
          {thread.viewer_kind === "student"
            ? "Ask your teacher about anything in this lesson."
            : "Nothing here yet. Your student can ask about anything in this lesson."}
        </p>
      ) : null}

      <div className="flex flex-col gap-4">
        {thread.comments.map((c) => (
          <div key={c.comment_id} data-testid={`comment-${c.comment_id}`}>
            <div className="flex items-baseline gap-2">
              <span className="font-sans text-[14px] font-semibold text-[var(--text-primary)]">
                {c.deleted ? "—" : c.author_name}
              </span>
              <span className="font-mono text-[11px] text-[var(--text-tertiary)]">{whenLabel(c.created_at)}</span>
              {c.edited_at ? (
                <span className="font-mono text-[11px] text-[var(--text-tertiary)]">edited</span>
              ) : null}
            </div>

            {editing === c.comment_id ? (
              <div className="mt-1.5 flex flex-col gap-2">
                <textarea
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  rows={3}
                  data-testid="comment-edit-input"
                  className="w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]"
                />
                <div className="flex gap-2">
                  <Button onPress={() => saveEdit(c.comment_id)} data-testid="comment-edit-save">
                    Save
                  </Button>
                  <Button variant="ghost" onPress={() => setEditing(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p
                className={cn(
                  "mt-1 whitespace-pre-wrap font-sans text-[14px]",
                  c.deleted ? "italic text-[var(--text-tertiary)]" : "text-[var(--text-secondary)]",
                )}
              >
                {c.body}
              </p>
            )}

            {!c.deleted && editing !== c.comment_id ? (
              <div className="mt-1 flex gap-3">
                {mine(c) && withinEditWindow(c.created_at) ? (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(c.comment_id);
                      setEditDraft(c.body);
                    }}
                    data-testid={`comment-edit-${c.comment_id}`}
                    className="font-sans text-[12px] text-[var(--text-tertiary)] hover:underline"
                  >
                    Edit
                  </button>
                ) : null}
                {mine(c) || thread.viewer_kind === "instructor" ? (
                  <button
                    type="button"
                    onClick={() => remove(c.comment_id)}
                    data-testid={`comment-delete-${c.comment_id}`}
                    className="font-sans text-[12px] text-[var(--text-tertiary)] hover:underline"
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {thread.can_post ? (
        <div className="mt-5 flex flex-col gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={
              thread.viewer_kind === "student" ? "Ask a question about this lesson…" : "Reply to your student…"
            }
            data-testid="comment-input"
            className="w-full rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]"
          />
          <div>
            <Button onPress={post} disabled={busy || !draft.trim()} data-testid="comment-post">
              <Icon name="send" size={14} /> Post
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-5 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="comments-closed">
          This conversation is closed. You can still read what was said.
        </p>
      )}
    </Card>
  );
}

export default CommentThread;
